/**
 * A base class for the Decorator pattern using ES6 Proxy.
 *
 * Subclasses override specific methods to add behavior; all other property
 * access and method calls are transparently forwarded to the wrapped target.
 *
 * Inspired by Ruby's SimpleDelegator — the delegator "becomes" the target for
 * any property it doesn't explicitly define itself.
 *
 * @example
 *   class LoggingRequest extends SimpleDelegator {
 *     perform() {
 *       console.log('Performing request...');
 *       return this.__target__.perform();
 *     }
 *   }
 *
 *   const request = new LoggingRequest(new FetchRequest('GET', '/api'));
 *   request.perform(); // logs, then delegates
 *   request.url;       // forwarded to the underlying FetchRequest
 */
class SimpleDelegator {
  constructor(target) {
    this.__target__ = target;
    return new Proxy(this, {
      get(self, prop) {
        if (prop in self) {
          return self[prop];
        }

        const value = self.__target__[prop];
        return typeof value === 'function' ? value.bind(self.__target__) : value;
      },
      set(self, prop, value) {
        let target = self;
        if (prop in self.__target__ && !Object.getOwnPropertyDescriptor(self, prop)) {
          target = self.__target__;
        }
        return Reflect.set(target, prop, value);
      },
    });
  }
}

export default SimpleDelegator;
